import { Store } from "@redotech/util/store";
import { epochSecondsToInstant } from "@redotech/util/temporal";
import { localStore } from "@redotech/web-util/storage";
import jwtDecode from "jwt-decode";
import {
  ReactNode,
  createContext,
  memo,
  useContext,
  useEffect,
  useLayoutEffect,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";

export interface VendorAuth {
  vendorId: string;
  email: string;
  token: string;
  expiration: Temporal.Instant;
}

export const VendorAuthContext = createContext<VendorAuth | undefined>(
  undefined,
);

export type SetVendorAuth = (auth: VendorAuth | undefined) => void;

export const SetVendorAuthContext = createContext<SetVendorAuth | undefined>(
  undefined,
);

export const VENDOR_TOKEN_KEY = "marketplace.vendor_auth_token";

/**
 * Local storage for vendor auth token.
 */
export const vendorAuthStore: Store<string | undefined> =
  localStore(VENDOR_TOKEN_KEY);

export function parseVendorAuth(token: string): VendorAuth | undefined {
  try {
    const decoded = jwtDecode<any>(token);
    const vendorId = decoded.sub;
    const email = decoded.email;

    if (
      !vendorId ||
      typeof vendorId !== "string" ||
      !email ||
      typeof email !== "string"
    ) {
      throw new TypeError("Not a valid VendorAuth object");
    }

    return {
      vendorId,
      email,
      token,
      expiration: epochSecondsToInstant(decoded.exp),
    };
  } catch (error) {
    console.error("Failed to parse vendor token", error);
    return undefined;
  }
}

/** @returns whether the auth is expired (or expires within the next second) */
function isExpired(expiration: Temporal.Instant): boolean {
  const duration = expiration.since(Temporal.Now.instant());
  // Give ourselves 1 second leeway so it doesn't expire while trying to load the page
  return (
    Temporal.Duration.compare(
      Temporal.Duration.from({ seconds: 1 }),
      duration,
    ) > 0
  );
}

export const VendorAuthRequired = memo(function VendorAuthRequired({
  children,
}: {
  children: ReactNode | ReactNode[];
}) {
  const auth = useContext(VendorAuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  useLayoutEffect(() => {
    if (!auth) {
      const next = `${location.pathname}${location.search}${location.hash}`;
      navigate(`/login?next=${encodeURIComponent(next)}`);
    }
  }, [auth, navigate, location]);

  return auth ? <>{children}</> : null;
});

export const VendorAuthProvider = memo(function VendorAuthProvider({
  children,
}: {
  children: ReactNode | ReactNode[];
}) {
  const [auth, setAuth] = useState<VendorAuth | undefined>(() => {
    // Check for existing token
    const token = vendorAuthStore.get();
    if (!token) {
      return undefined;
    }

    const parsed = parseVendorAuth(token);
    if (!parsed || isExpired(parsed.expiration)) {
      vendorAuthStore.set(undefined);
      return undefined;
    }

    return parsed;
  });

  useEffect(() => {
    // Clear the local storage if the auth is undefined
    if (!auth) {
      vendorAuthStore.set(undefined);
      return;
    }

    // Store the token
    vendorAuthStore.set(auth.token);

    // Delete the auth when it expires
    const duration = auth.expiration.since(Temporal.Now.instant());
    const timeout = setTimeout(
      () => setAuth(undefined),
      Math.min(2_147_483_647, duration.total("milliseconds")),
    );
    return () => clearTimeout(timeout);
  }, [auth]);

  return (
    <VendorAuthContext.Provider value={auth}>
      <SetVendorAuthContext.Provider value={setAuth}>
        {children}
      </SetVendorAuthContext.Provider>
    </VendorAuthContext.Provider>
  );
});
